import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  MapPin, Calendar, Clock, User, Phone, Mail, Users, MessageSquare,
  ArrowLeft, CheckCircle, Loader2, Car, Leaf, Send, Plane, X,
  CreditCard, Banknote, Smartphone, Crosshair, Share2, Navigation
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { GooglePlacesAutocomplete, GoogleMapDisplay } from '../components/GoogleMaps';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '../components/LanguageSelector';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import axios from 'axios';
import { format, addHours, setHours, setMinutes, isBefore, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuickBookingPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [date, setDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  
  const prefillPickup = searchParams.get('pickup') || '';
  const prefillDest = searchParams.get('destination') || '';
  const prefillPrice = searchParams.get('price') || '';
  const prefillRoundTrip = searchParams.get('roundtrip') === '1';
  const paymentCancelled = searchParams.get('cancelled') === 'true';

  // Handle payment cancelled return
  useEffect(() => {
    if (paymentCancelled) {
      toast.error('Paiement annulé. Votre demande a été enregistrée, vous pouvez réessayer.');
    }
  }, [paymentCancelled]);

  const PRICE_MAP = {
    'Nice Centre': { oneWay: 25, roundTrip: 45 },
    'Nice Centre-Ville': { oneWay: 25, roundTrip: 45 },
    'Monaco / Monte-Carlo': { oneWay: 65, roundTrip: 120 },
    'Cannes / La Croisette': { oneWay: 60, roundTrip: 110 },
    'Saint-Tropez': { oneWay: 180, roundTrip: 300 },
  };

  const [roundTrip, setRoundTrip] = useState(prefillRoundTrip);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [pickupCoords, setPickupCoords] = useState(null);
  const [destCoords, setDestCoords] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [privilegedInfo, setPrivilegedInfo] = useState(null); // {client_name, fixed_price} if matched
  const [stops, setStops] = useState([]);
  const [pinMode, setPinMode] = useState(false);
  const [pinCoords, setPinCoords] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const WAIT_RATE = 0.35; // €/min

  const getPrice = () => {
    const match = PRICE_MAP[prefillDest];
    if (match) return roundTrip ? match.roundTrip : match.oneWay;
    return prefillPrice || null;
  };

  const handlePinPlace = (pin) => {
    setPinCoords(pin);
    handleChange('pickup_address', pin.address);
    setPickupCoords({ lat: pin.lat, lng: pin.lng });
    if (destCoords) calculateEstimate({ lat: pin.lat, lng: pin.lng }, destCoords);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error('La geolocalisation n\'est pas disponible sur votre appareil');
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setPinCoords({ lat, lng, address: `${lat.toFixed(6)}, ${lng.toFixed(6)}` });
        setPickupCoords({ lat, lng });
        setPinMode(true);
        // Reverse geocode
        if (window.google) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results[0]) {
              const addr = results[0].formatted_address;
              setPinCoords(prev => ({ ...prev, address: addr }));
              handleChange('pickup_address', addr);
            }
          });
        }
        if (destCoords) calculateEstimate({ lat, lng }, destCoords);
        setGeoLoading(false);
        toast.success('Position detectee');
      },
      (err) => {
        setGeoLoading(false);
        toast.error('Impossible d\'obtenir votre position. Verifiez les permissions.');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const sharePosition = (method) => {
    const text = 'Bonjour, je souhaite partager ma position pour ma course YOUMI GREEN VTC.';
    if (method === 'whatsapp') {
      window.location.href = `https://wa.me/33753202093?text=${encodeURIComponent(text)}`;
    } else {
      window.location.href = `https://maps.google.com/maps?q=my+location`;
    }
  };

  const calculateEstimate = async (pCoords, dCoords) => {
    if (!pCoords || !dCoords) return;
    setEstimateLoading(true);
    try {
      const wpCoords = stops.filter(s => s.coords).map(s => s.coords);
      // Get pickup hour from form time if available
      let pickupHour = new Date().getHours();
      if (form.time) {
        try { pickupHour = parseInt(form.time.split(':')[0]); } catch {}
      }
      let pickupDateStr = '';
      if (date) {
        try { pickupDateStr = format(date, 'yyyy-MM-dd'); } catch {}
      }
      const res = await axios.post(`${API}/bookings/estimate`, null, {
        params: {
          pickup_lat: pCoords.lat,
          pickup_lng: pCoords.lng,
          dest_lat: dCoords.lat,
          dest_lng: dCoords.lng,
          pickup_hour: pickupHour,
          pickup_address: form.pickup_address || '',
          dest_address: form.destination_address || '',
          pickup_date: pickupDateStr,
          waypoints: wpCoords.length > 0 ? JSON.stringify(wpCoords) : '',
          phone: form.phone || ''
        }
      });
      setEstimate(res.data);
      if (res.data?.is_privileged_price) {
        setPrivilegedInfo({
          client_name: res.data.privileged_client_name,
          fixed_price: res.data.price,
        });
      } else {
        setPrivilegedInfo(null);
      }
    } catch (e) { console.error(e); }
    finally { setEstimateLoading(false); }
  };

  const addStop = () => {
    if (stops.length >= 3) return;
    setStops(prev => [...prev, { address: '', coords: null, waitMinutes: 0, customWait: '' }]);
  };
  const removeStop = (i) => setStops(prev => prev.filter((_, idx) => idx !== i));
  const updateStop = (i, field, val) => setStops(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: val } : s));
  const totalWaitCost = stops.reduce((sum, s) => sum + (s.waitMinutes || 0) * WAIT_RATE, 0);
  const getBasePrice = () => {
    // Privileged client price overrides everything
    if (privilegedInfo?.fixed_price != null) return parseFloat(privilegedInfo.fixed_price);
    const fixed = getPrice();
    if (fixed) return parseFloat(fixed);
    if (estimate) return estimate.price;
    return null;
  };
  const totalPrice = getBasePrice() !== null ? (getBasePrice() + totalWaitCost) : null;

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    pickup_address: prefillPickup,
    destination_address: prefillDest,
    flight_number: '',
    time: '',
    passengers: 1,
    message: prefillPrice ? `Tarif fixe: ${prefillRoundTrip ? 'A/R' : 'aller simple'} ${prefillPrice}€` : ''
  });

  const airportTerms = ['roport', 'airport', 'terminal 1', 'terminal 2', 'costes et bellonte', 'costes et bello', '06206'];
  const checkAirport = (addr) => addr && airportTerms.some(t => addr.toLowerCase().includes(t));
  const isAirportPickup = checkAirport(prefillPickup) || checkAirport(form.pickup_address);
  const isAirportDest = checkAirport(prefillDest) || checkAirport(form.destination_address);
  const isAirportTransfer = isAirportPickup || isAirportDest;

  useEffect(() => {
    if (pickupCoords && destCoords) calculateEstimate(pickupCoords, destCoords);
  }, [stops.map(s => s.coords?.lat).join(',')]);

  // Debounced privileged price check when phone changes — also lookup directly when no estimate yet
  useEffect(() => {
    const phone = (form.phone || '').trim();
    if (!phone || phone.length < 6) {
      setPrivilegedInfo(null);
      return;
    }
    const handle = setTimeout(async () => {
      // If we have coords, re-run full estimate (cheapest single source of truth)
      if (pickupCoords && destCoords) {
        calculateEstimate(pickupCoords, destCoords);
        return;
      }
      // Otherwise just call the privilege endpoint for early UX feedback
      try {
        const r = await axios.get(`${API}/check-privileged-price`, {
          params: {
            phone,
            pickup_address: form.pickup_address || '',
            dest_address: form.destination_address || '',
          }
        });
        if (r.data?.has_fixed_price) {
          setPrivilegedInfo({ client_name: r.data.client_name, fixed_price: r.data.fixed_price });
        } else {
          setPrivilegedInfo(null);
        }
      } catch (e) { /* silent */ }
    }, 600);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.phone, pickupCoords, destCoords]);

  const timeSlots = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      timeSlots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const canSubmit = () => {
    const base = form.first_name && form.last_name && form.phone && 
           form.pickup_address && form.destination_address && 
           date && form.time;
    if (isAirportTransfer) return base && form.flight_number;
    if (base && date && form.time) {
      const [h, m] = form.time.split(':').map(Number);
      const selected = setMinutes(setHours(new Date(date), h), m);
      if (isBefore(selected, addHours(new Date(), 1))) return false;
    }
    return base;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit()) return;
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/quick-booking`, {
        ...form,
        date: format(date, 'yyyy-MM-dd'),
        stops: stops.filter(s => s.address).map(s => ({ address: s.address, wait_minutes: s.waitMinutes })),
        total_wait_cost: totalWaitCost.toFixed(2),
        total_price: totalPrice?.toFixed(2) || '',
        payment_method: paymentMethod,
        pickup_lat: pinCoords?.lat || pickupCoords?.lat || null,
        pickup_lng: pinCoords?.lng || pickupCoords?.lng || null,
        dest_lat: destCoords?.lat || null,
        dest_lng: destCoords?.lng || null,
        pin_pickup: !!pinCoords,
      });
      setBookingId(res.data.booking_id);

      // Google Ads conversion
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-17986670215/B1EqCO7QpYEcEIed24BD',
          'transaction_id': res.data.booking_id,
        });
      }

      // Online payment methods: redirect to Stripe Checkout
      if (totalPrice && totalPrice > 0 && ['stripe', 'apple_pay', 'google_pay', 'paypal'].includes(paymentMethod)) {
        try {
          const checkoutRes = await axios.post(
            `${API}/quick-booking/${res.data.booking_id}/checkout`,
            null,
            { params: { origin_url: window.location.origin, payment_method: paymentMethod } }
          );
          window.location.href = checkoutRes.data.checkout_url;
          return;
        } catch (payErr) {
          console.error('Checkout error:', payErr);
          setSuccess(true);
        }
      } else {
        // Cash or card on board — just confirm
        setSuccess(true);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="glass-card rounded-2xl p-8">
            <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
              <CheckCircle className="w-10 h-10 text-emerald-500" />
            </div>
            <h1 className="text-2xl font-bold text-white font-['Outfit'] mb-4" data-testid="quick-booking-success">
              {t('quick_success_title')}
            </h1>
            <p className="text-zinc-400 mb-8">
              {t('quick_success_desc')}
            </p>
            <div className="flex flex-col gap-3">
              <Button className="btn-primary" onClick={() => navigate('/')} data-testid="back-home-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('back_home')}
              </Button>
              {/* Share position buttons */}
              <div className="border border-zinc-700 rounded-xl p-4 space-y-3">
                <p className="text-zinc-300 text-sm font-medium flex items-center gap-2">
                  <Navigation className="w-4 h-4 text-emerald-500" />
                  {t('share_position_title')}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => sharePosition('whatsapp')}
                    className="flex-1 flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white text-sm py-2.5 rounded-lg transition-colors"
                    data-testid="share-position-whatsapp"
                  >
                    <Share2 className="w-4 h-4" /> {t('share_whatsapp')}
                  </button>
                  <button
                    onClick={() => sharePosition('sms')}
                    className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm py-2.5 rounded-lg transition-colors"
                    data-testid="share-position-sms"
                  >
                    <Phone className="w-4 h-4" /> {t('share_sms')}
                  </button>
                </div>
              </div>
              <button 
                onClick={() => navigate('/auth')}
                className="text-emerald-500 hover:text-emerald-400 text-sm"
                data-testid="create-account-link"
              >
                {t('quick_create_account')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#09090b]/95 backdrop-blur-md border-b border-zinc-800">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-zinc-400 hover:text-white transition" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
            <img src="/logo-youmi.png" alt="YOUMI GREEN VTC" className="w-10 h-10" />
            <span className="text-lg font-bold text-white font-['Outfit']">YOUMI <span className="text-emerald-500">GREEN</span></span>
          </button>
          <div className="flex items-center gap-3">
            <LanguageSelector variant="minimal" />
            <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white" onClick={() => navigate('/auth')} data-testid="login-link">
              {t('nav_login')}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 rounded-full text-emerald-400 text-sm mb-4">
            <Car className="w-4 h-4" />
            {t('quick_badge')}
          </div>
          <h1 className="text-3xl font-bold text-white font-['Outfit'] mb-2" data-testid="quick-booking-title">
            {t('quick_title')}
          </h1>
          <p className="text-zinc-400">{t('quick_subtitle')}</p>
        </div>

        {/* Privileged client welcome banner */}
        {privilegedInfo && (
          <div
            className="mb-6 rounded-2xl p-5 bg-gradient-to-r from-amber-500/15 via-amber-400/10 to-emerald-500/10 border border-amber-400/40 shadow-[0_0_25px_-5px_rgba(251,191,36,0.35)]"
            data-testid="privileged-welcome-banner"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-400/20 flex items-center justify-center shrink-0">
                <Leaf className="w-5 h-5 text-amber-300" />
              </div>
              <div className="flex-1">
                <p className="text-amber-200 font-semibold text-base" data-testid="privileged-welcome-text">
                  Bienvenue {privilegedInfo.client_name} —{' '}
                  <span className="text-emerald-300">
                    Votre tarif privilégié de {Number(privilegedInfo.fixed_price).toFixed(2)}€ a été appliqué
                  </span>
                </p>
                <p className="text-xs text-zinc-400 mt-1">
                  Tarif négocié appliqué automatiquement, sans supplément nuit ni saisonnalité.
                </p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Identity */}
          <div className="glass-card rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-emerald-500" />
              {t('quick_identity')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">{t('auth_firstname')} *</Label>
                <Input
                  value={form.first_name}
                  onChange={(e) => handleChange('first_name', e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-white mt-1"
                  required
                  data-testid="quick-firstname"
                />
              </div>
              <div>
                <Label className="text-zinc-300">{t('auth_lastname')} *</Label>
                <Input
                  value={form.last_name}
                  onChange={(e) => handleChange('last_name', e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-white mt-1"
                  required
                  data-testid="quick-lastname"
                />
              </div>
              <div>
                <Label className="text-zinc-300 flex items-center gap-1">
                  <Phone className="w-3 h-3" /> {t('auth_phone')} *
                </Label>
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="+33 6 12 34 56 78"
                  className="bg-zinc-900 border-zinc-700 text-white mt-1"
                  required
                  data-testid="quick-phone"
                />
              </div>
              <div>
                <Label className="text-zinc-300 flex items-center gap-1">
                  <Mail className="w-3 h-3" /> {t('auth_email')} ({t('quick_optional')})
                </Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-white mt-1"
                  data-testid="quick-email"
                />
              </div>
            </div>
          </div>

          {/* Route */}
          <div className="glass-card rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-emerald-500" />
              {t('quick_route')}
            </h2>
            <div className="space-y-4">
              <div>
                <Label className="text-zinc-300">{t('booking_pickup')} *</Label>
                <div className="mt-1">
                  <GooglePlacesAutocomplete
                    onSelect={(address) => {
                      handleChange('pickup_address', address.display_name || address);
                      setPickupCoords({ lat: address.lat, lng: address.lng });
                      if (destCoords) calculateEstimate({ lat: address.lat, lng: address.lng }, destCoords);
                    }}
                    placeholder={t('booking_pickup')}
                    value={prefillPickup}
                  />
                </div>
                {form.pickup_address && (
                  <p className="text-emerald-400 text-xs mt-1 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> {form.pickup_address}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-zinc-300">{t('booking_destination')} *</Label>
                <div className="mt-1">
                  <GooglePlacesAutocomplete
                    onSelect={(address) => {
                      handleChange('destination_address', address.display_name || address);
                      setDestCoords({ lat: address.lat, lng: address.lng });
                      if (pickupCoords) calculateEstimate(pickupCoords, { lat: address.lat, lng: address.lng });
                    }}
                    placeholder={t('booking_destination')}
                    value={prefillDest}
                  />
                </div>
                {form.destination_address && (
                  <p className="text-emerald-400 text-xs mt-1 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> {form.destination_address}
                  </p>
                )}
              </div>
            </div>

            {/* Google Map */}
            {(pickupCoords || destCoords) && (
              <div className="mt-4 space-y-2">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => { setPinMode(!pinMode); if (pinMode) { setPinCoords(null); } }}
                    className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      pinMode ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-500'
                    }`}
                    data-testid="toggle-pin-mode"
                  >
                    <Crosshair className="w-3.5 h-3.5" />
                    {pinMode ? t('pin_mode_active') : t('pin_place')}
                  </button>
                  <button
                    type="button"
                    onClick={useMyLocation}
                    disabled={geoLoading}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-emerald-500 hover:text-emerald-400 transition-colors"
                    data-testid="use-my-location"
                  >
                    {geoLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Navigation className="w-3.5 h-3.5" />}
                    {t('my_location')}
                  </button>
                </div>
                {pinCoords && (
                  <div className="flex items-center gap-2 text-xs bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
                    <Crosshair className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    <span className="text-emerald-300 truncate">{pinCoords.address}</span>
                    <button type="button" onClick={() => { setPinCoords(null); setPinMode(false); }} className="text-zinc-400 hover:text-red-400 ml-auto shrink-0">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                <GoogleMapDisplay
                  pickupCoords={pickupCoords}
                  destCoords={destCoords}
                  height="280px"
                  showRoute={pickupCoords && destCoords}
                  interactive={pinMode}
                  pinCoords={pinCoords}
                  onPinPlace={handlePinPlace}
                />
              </div>
            )}
          </div>

          {/* Multi-stops */}
          <div className="glass-card rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-emerald-500" />
              {t('stops_title')}
            </h2>
            {stops.map((stop, i) => (
              <div key={i} className="mb-4 p-4 bg-zinc-800/50 rounded-xl border border-zinc-700 relative">
                <button onClick={() => removeStop(i)} className="absolute top-2 right-2 text-zinc-500 hover:text-red-400 transition" data-testid={`remove-stop-${i}`}>
                  <X className="w-4 h-4" />
                </button>
                <Label className="text-zinc-300 text-sm">{t('stop_address')} {i + 1}</Label>
                <div className="mt-1 mb-3">
                  <GooglePlacesAutocomplete
                    onSelect={(addr) => { updateStop(i, 'address', addr.display_name || addr); updateStop(i, 'coords', { lat: addr.lat, lng: addr.lng }); }}
                    placeholder={t('stop_placeholder')}
                  />
                </div>
                <Label className="text-zinc-300 text-sm">{t('stop_wait_time')}</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {[0, 5, 10, 15, 20, 30].map(m => (
                    <button key={m} onClick={() => updateStop(i, 'waitMinutes', m)}
                      className={`px-3 py-1 rounded-lg text-xs font-medium border transition ${stop.waitMinutes === m && !stop.customWait ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-500'}`}
                      data-testid={`wait-${i}-${m}`}>
                      {m === 0 ? t('stop_no_wait') : `${m} min`}
                    </button>
                  ))}
                  <Input
                    type="number" min="0" max="120" placeholder={t('stop_custom')}
                    value={stop.customWait}
                    onChange={(e) => { updateStop(i, 'customWait', e.target.value); updateStop(i, 'waitMinutes', parseInt(e.target.value) || 0); }}
                    className="w-24 bg-zinc-800 border-zinc-700 text-white text-xs h-8"
                  />
                </div>
                {stop.waitMinutes > 0 && (
                  <p className="text-emerald-400 text-xs mt-2">{t('stop_wait_cost')}: {(stop.waitMinutes * WAIT_RATE).toFixed(2)}€</p>
                )}
              </div>
            ))}
            {stops.length < 3 && (
              <Button variant="outline" onClick={addStop} className="w-full border-dashed border-zinc-600 text-zinc-400 hover:text-emerald-400 hover:border-emerald-500" data-testid="add-stop-btn">
                + {t('stop_add')}
              </Button>
            )}
          </div>

          {/* Round trip checkbox - shown when price is available */}
          {prefillPrice && (
            <div className="glass-card rounded-2xl p-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={roundTrip}
                  onChange={(e) => setRoundTrip(e.target.checked)}
                  className="w-5 h-5 accent-emerald-500 rounded"
                  data-testid="quick-roundtrip"
                />
                <span className="text-white font-semibold">{t('round_trip_label')}</span>
              </label>
              {prefillDest === 'Saint-Tropez' && roundTrip && (
                <p className="text-zinc-500 text-xs mt-2 italic ml-8">* {t('round_trip_same_day')}</p>
              )}
              <div className="mt-4 space-y-2">
                <div className="flex justify-between items-center text-sm px-1">
                  <span className="text-zinc-400">{t('price_base_ride')}</span>
                  {privilegedInfo ? (
                    <span className="text-white">
                      <span className="line-through text-zinc-500 mr-2">{getPrice()}€</span>
                      <span className="text-amber-300 font-semibold">{Number(privilegedInfo.fixed_price).toFixed(2)}€</span>
                    </span>
                  ) : (
                    <span className="text-white">{getPrice()}€</span>
                  )}
                </div>
                {totalWaitCost > 0 && (
                  <div className="flex justify-between items-center text-sm px-1">
                    <span className="text-zinc-400">{t('price_stops_wait')}</span>
                    <span className="text-amber-400">+{totalWaitCost.toFixed(2)}€</span>
                  </div>
                )}
                <div className="flex justify-between items-center p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                  <span className="text-emerald-400 font-semibold">
                    {privilegedInfo ? 'Tarif privilégié' : (roundTrip ? t('round_trip_price') : t('one_way_price'))}
                  </span>
                  <span className="text-3xl font-bold text-emerald-500 font-['Outfit']" data-testid="prefill-total-price">
                    {(getBasePrice() + totalWaitCost).toFixed(2)}€
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Price estimate - auto-calculated when both addresses selected (no prefill) */}
          {!prefillPrice && estimateLoading && (
            <div className="glass-card rounded-2xl p-6 text-center">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-500 mx-auto mb-2" />
              <p className="text-zinc-400 text-sm">{t('estimate_loading')}</p>
            </div>
          )}
          {!prefillPrice && estimate && (
            <div className="glass-card rounded-2xl p-6" data-testid="price-estimate">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Car className="w-5 h-5 text-emerald-500" />
                {t('estimate_title')}
              </h2>
              <div className="space-y-2 mb-3">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Distance</span>
                  <span className="text-white">{estimate.distance_km?.toFixed(1)} km</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">{t('estimate_duration')}</span>
                  <span className="text-white">~{Math.round(estimate.duration_min || 0)} min</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">{t('price_base_ride')}</span>
                  {estimate.is_privileged_price && estimate.original_price ? (
                    <span className="text-white">
                      <span className="line-through text-zinc-500 mr-2">{estimate.original_price.toFixed(2)}€</span>
                      <span className="text-amber-300 font-semibold">{estimate.price?.toFixed(2)}€</span>
                    </span>
                  ) : (
                    <span className="text-white">{estimate.price?.toFixed(2)}€</span>
                  )}
                </div>
                {totalWaitCost > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">{t('price_stops_wait')}</span>
                    <span className="text-amber-400">+{totalWaitCost.toFixed(2)}€</span>
                  </div>
                )}
              </div>
              <div className="flex justify-between items-center p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                <span className="text-emerald-400 font-semibold">{t('estimate_price_ttc')}</span>
                <span className="text-3xl font-bold text-emerald-500 font-['Outfit']">{totalPrice?.toFixed(2)}€</span>
              </div>
              <p className="text-xs text-zinc-500 mt-2 text-center">{t('estimate_note')}</p>
            </div>
          )}

          {/* Date & Time */}
          <div className="glass-card rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-emerald-500" />
              {t('booking_step_datetime')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">{t('booking_date')} *</Label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full mt-1 bg-zinc-900 border-zinc-700 text-white justify-start"
                      data-testid="quick-date"
                    >
                      <Calendar className="w-4 h-4 mr-2 text-emerald-500" />
                      {date ? format(date, 'PPP', { locale: fr }) : t('booking_select')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-700">
                    <CalendarComponent
                      mode="single"
                      selected={date}
                      onSelect={(d) => { setDate(d); setCalendarOpen(false); }}
                      disabled={(d) => isBefore(d, startOfDay(new Date()))}
                      locale={fr}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-zinc-300">{t('booking_time')} *</Label>
                <select
                  value={form.time}
                  onChange={(e) => handleChange('time', e.target.value)}
                  className="w-full mt-1 bg-zinc-900 border border-zinc-700 text-white rounded-md px-3 py-2 h-10"
                  data-testid="quick-time"
                >
                  <option value="">{t('booking_select')}</option>
                  {timeSlots.map(slot => (
                    <option key={slot} value={slot}>{slot}</option>
                  ))}
                </select>
              </div>
            </div>
            {date && form.time && (() => {
              const [h, m] = form.time.split(':').map(Number);
              const selected = setMinutes(setHours(new Date(date), h), m);
              return isBefore(selected, addHours(new Date(), 1));
            })() && (
              <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                La réservation doit être faite au moins 1 heure à l'avance
              </div>
            )}
          </div>

          {/* Flight number - shown for airport transfers */}
          {isAirportTransfer && (
            <div className="glass-card rounded-2xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Plane className="w-5 h-5 text-emerald-500" />
                {t('flight_number_title')}
              </h2>
              <div>
                <Label className="text-zinc-300">{t('flight_number_label')} *</Label>
                <Input
                  value={form.flight_number}
                  onChange={(e) => handleChange('flight_number', e.target.value.toUpperCase())}
                  placeholder="Ex: AF1234, EZY4521"
                  className="bg-zinc-900 border-zinc-700 text-white mt-1"
                  required
                  data-testid="quick-flight"
                />
                <p className="text-zinc-500 text-xs mt-1">{t('flight_number_hint')}</p>
              </div>
            </div>
          )}

          {/* Options */}
          <div className="glass-card rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-500" />
              {t('quick_options')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">{t('quick_passengers')}</Label>
                <select
                  value={form.passengers}
                  onChange={(e) => handleChange('passengers', parseInt(e.target.value))}
                  className="w-full mt-1 bg-zinc-900 border border-zinc-700 text-white rounded-md px-3 py-2 h-10"
                  data-testid="quick-passengers"
                >
                  {[1, 2, 3, 4].map(n => (
                    <option key={n} value={n}>{n} {n > 1 ? 'passagers' : 'passager'}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-zinc-300 flex items-center gap-1">
                  <MessageSquare className="w-3 h-3" /> {t('quick_message')}
                </Label>
                <Textarea
                  value={form.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  placeholder={t('quick_message_placeholder')}
                  className="bg-zinc-900 border-zinc-700 text-white mt-1"
                  rows={3}
                  data-testid="quick-message"
                />
              </div>
            </div>
          </div>

          {/* CO2 badge */}
          <div className="p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/30">
            <div className="flex items-center gap-2 text-emerald-400 text-sm">
              <Leaf className="w-4 h-4" />
              <span>{t('booking_co2')}</span>
            </div>
          </div>

          {/* Payment Method Selection */}
          {totalPrice && totalPrice > 0 && (
            <div className="glass-card rounded-xl p-5">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-500" />
                Mode de paiement
              </h3>
              <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-2">
                <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${paymentMethod === 'stripe' ? 'border-emerald-500 bg-emerald-500/10' : 'border-zinc-700 hover:border-zinc-600'}`}>
                  <RadioGroupItem value="stripe" id="qb-stripe" data-testid="qb-payment-stripe" />
                  <CreditCard className="w-5 h-5 text-zinc-400" />
                  <div className="flex-1">
                    <p className="font-medium text-white text-sm">Carte bancaire</p>
                    <p className="text-xs text-zinc-400">Visa, Mastercard - Paiement securise</p>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${paymentMethod === 'apple_pay' ? 'border-emerald-500 bg-emerald-500/10' : 'border-zinc-700 hover:border-zinc-600'}`}>
                  <RadioGroupItem value="apple_pay" id="qb-apple" data-testid="qb-payment-apple" />
                  <Smartphone className="w-5 h-5 text-zinc-400" />
                  <div className="flex-1">
                    <p className="font-medium text-white text-sm">Apple Pay</p>
                    <p className="text-xs text-zinc-400">Paiement rapide et securise</p>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${paymentMethod === 'google_pay' ? 'border-emerald-500 bg-emerald-500/10' : 'border-zinc-700 hover:border-zinc-600'}`}>
                  <RadioGroupItem value="google_pay" id="qb-google" data-testid="qb-payment-google" />
                  <Smartphone className="w-5 h-5 text-zinc-400" />
                  <div className="flex-1">
                    <p className="font-medium text-white text-sm">Google Pay</p>
                    <p className="text-xs text-zinc-400">Paiement rapide et securise</p>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${paymentMethod === 'paypal' ? 'border-emerald-500 bg-emerald-500/10' : 'border-zinc-700 hover:border-zinc-600'}`}>
                  <RadioGroupItem value="paypal" id="qb-paypal" data-testid="qb-payment-paypal" />
                  <CreditCard className="w-5 h-5 text-zinc-400" />
                  <div className="flex-1">
                    <p className="font-medium text-white text-sm">PayPal</p>
                    <p className="text-xs text-zinc-400">Paiement via votre compte PayPal</p>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${paymentMethod === 'card_onboard' ? 'border-emerald-500 bg-emerald-500/10' : 'border-zinc-700 hover:border-zinc-600'}`}>
                  <RadioGroupItem value="card_onboard" id="qb-card-onboard" data-testid="qb-payment-card-onboard" />
                  <CreditCard className="w-5 h-5 text-zinc-400" />
                  <div className="flex-1">
                    <p className="font-medium text-white text-sm">Carte a bord</p>
                    <p className="text-xs text-zinc-400">Paiement au chauffeur a bord du vehicule</p>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${paymentMethod === 'cash' ? 'border-emerald-500 bg-emerald-500/10' : 'border-zinc-700 hover:border-zinc-600'}`}>
                  <RadioGroupItem value="cash" id="qb-cash" data-testid="qb-payment-cash" />
                  <Banknote className="w-5 h-5 text-zinc-400" />
                  <div className="flex-1">
                    <p className="font-medium text-white text-sm">Especes</p>
                    <p className="text-xs text-zinc-400">Paiement au chauffeur a bord du vehicule</p>
                  </div>
                </label>
              </RadioGroup>
            </div>
          )}

          {/* Submit */}
          <Button
            type="submit"
            className="w-full btn-primary text-lg py-6"
            disabled={!canSubmit() || loading}
            data-testid="quick-submit-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
            ) : (
              <Send className="w-5 h-5 mr-2" />
            )}
            {loading ? t('common_loading') : (
              ['cash', 'card_onboard'].includes(paymentMethod) || !totalPrice ? t('quick_submit') : 'Payer et reserver'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default QuickBookingPage;
